//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import angular from 'angular';
import { INgRedux } from 'ng-redux';

import { ICuralateStore } from '@models/AppState';
import { streamCreationActions } from '@streams/actions/streamCreationActions';
import { StreamSourceTypes } from '@streams/constants/streamCreationConstants';

import '@streams-drawer/actions/streams-drawer-actions';
import '@streams-drawer/streams-drawer-standard-setup/streams-drawer-standard-setup';
import { getShouldNotAllowUgcStreams } from '@streams/selectors/getShouldNotAllowUgcStreams';
import { facebookInstagramSettingsActions } from '@account-settings/redux/facebook-instagram/facebookInstagramSettingsActions';

interface IStreamSuggestion {
  name: string;
  onCreate: () => void;
  shouldDisableSuggestion?: boolean;
}

export class StreamsDrawerStandardSetupContainer {
  private static mapStateToThis = (state: ICuralateStore) => {
    return {
      brandInstagramUsername:
        state.streams.streamsDrawer.brandInstagramUsername,
      isGraphApiConnected: !!state.facebookInstagramSettings
        .connectedInstagramAccount,
      shouldNotAllowUgcStreams: getShouldNotAllowUgcStreams(state),
    };
  };

  public streamSuggestions: IStreamSuggestion[];

  public brandInstagramUsername: string;
  public changeStreamName: (streamName: string) => void;
  public directAddSource: (
    sourceType: StreamSourceTypes,
    sourceValue?: string,
  ) => void;
  public isGraphApiConnected: boolean;
  public openNewStreamModal: () => void;
  public shouldNotAllowUgcStreams: boolean;

  private unsubscribe: () => void;

  public constructor($ngRedux: INgRedux, StreamsDrawerActions) {
    const actions = {
      ...StreamsDrawerActions,
      ...streamCreationActions,
    };

    $ngRedux.dispatch(facebookInstagramSettingsActions.loadConnections());

    this.unsubscribe = $ngRedux.connect(
      StreamsDrawerStandardSetupContainer.mapStateToThis,
      actions,
    )(this);
  }

  public $onInit() {
    // TODO DEV-40260: Handle edge case of unsuccessfully adding brand username to stream
    this.streamSuggestions = [
      {
        name: this.brandInstagramUsername
          ? `My Instagram @${this.brandInstagramUsername}`
          : 'My Instagram',
        onCreate: async () => {
          await this.changeStreamName(
            `My Instagram @${this.brandInstagramUsername}`,
          );
          await this.directAddSource(
            StreamSourceTypes.USER,
            this.brandInstagramUsername,
          );
          this.openNewStreamModal();
        },
        shouldDisableSuggestion: !this.isGraphApiConnected,
      },
      {
        name: 'Mentioned & Tagged',
        onCreate: async () => {
          await this.changeStreamName('Mentioned & Tagged');
          await this.directAddSource(StreamSourceTypes.MENTIONS_AND_TAGS);
          this.openNewStreamModal();
        },
        shouldDisableSuggestion:
          !this.isGraphApiConnected || this.shouldNotAllowUgcStreams,
      },
      {
        name: 'Brand Hashtags',
        onCreate: async () => {
          await this.changeStreamName('Brand Hashtags');
          this.openNewStreamModal();
        },
        shouldDisableSuggestion: this.shouldNotAllowUgcStreams,
      },
      {
        name: 'Campaign Hashtags',
        onCreate: async () => {
          await this.changeStreamName('Campaign Hashtags');
          this.openNewStreamModal();
        },
        shouldDisableSuggestion: this.shouldNotAllowUgcStreams,
      },
    ];
  }

  public $onDestroy() {
    this.unsubscribe();
  }
}

angular.module('curalate').component('streamsDrawerStandardSetupContainer', {
  controller: [
    '$ngRedux',
    'StreamsDrawerActions',
    StreamsDrawerStandardSetupContainer,
  ],
  template: `
        <streams-drawer-standard-setup
            on-open-new-stream-modal="$ctrl.openNewStreamModal()"
            stream-suggestions="$ctrl.streamSuggestions"
            should-not-allow-ugc-streams="$ctrl.shouldNotAllowUgcStreams"
            is-graph-api-connected="$ctrl.isGraphApiConnected"
        ></streams-drawer-standard-setup>
    `,
});
